import React from 'react';

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

export interface ConnectionStatusIndicatorProps {
  /** Current connection status */
  status: ConnectionStatus;
  /** Optional callback when user clicks to retry connection */
  onRetry?: () => void;
  /** Optional className for additional styling */
  className?: string;
  /** Position of the indicator */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

/**
 * ConnectionStatusIndicator Component
 * 
 * Displays the current WebSocket connection state in a corner of the game room.
 * Shows different visual states for:
 * - Connected: Green indicator (hidden by default, can be shown)
 * - Disconnected: Red indicator with retry option
 * - Reconnecting: Yellow/amber indicator with animation
 * 
 * Requirements: 10.3, 10.4
 */
export const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  status,
  onRetry,
  className = '',
  position = 'bottom-right',
}) => {
  // Position classes based on prop
  const positionClasses: Record<string, string> = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  // Status-specific styling
  const statusConfig: Record<ConnectionStatus, {
    bgColor: string;
    borderColor: string;
    textColor: string;
    dotColor: string;
    label: string;
    icon: string;
    animate: boolean;
  }> = {
    connected: {
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      textColor: 'text-green-400',
      dotColor: 'bg-green-500',
      label: 'Connected',
      icon: '✓',
      animate: false,
    },
    disconnected: {
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      textColor: 'text-red-400',
      dotColor: 'bg-red-500',
      label: 'Disconnected',
      icon: '✕',
      animate: false,
    },
    reconnecting: {
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      textColor: 'text-amber-400',
      dotColor: 'bg-amber-500',
      label: 'Reconnecting...',
      icon: '↻',
      animate: true,
    },
  };

  const config = statusConfig[status];

  // Don't show indicator when connected (optional: can be changed to always show)
  // For now, we'll show it but with a more subtle style when connected
  const isHidden = status === 'connected';

  if (isHidden) {
    return null;
  }

  return (
    <div
      className={`
        connection-status-indicator
        fixed ${positionClasses[position]}
        z-50
        ${className}
      `}
      data-testid="connection-status-indicator"
      data-status={status}
      role="status"
      aria-live="polite"
      aria-label={`Connection status: ${config.label}`}
    >
      <div
        className={`
          flex items-center gap-2
          px-4 py-2
          rounded-lg
          border
          backdrop-blur-sm
          shadow-lg
          ${config.bgColor}
          ${config.borderColor}
          transition-all duration-300
        `}
      >
        {/* Status Dot */}
        <span
          className={`
            w-2 h-2 rounded-full
            ${config.dotColor}
            ${config.animate ? 'animate-pulse' : ''}
          `}
          aria-hidden="true"
        />

        {/* Status Icon */}
        <span
          className={`
            text-sm font-medium
            ${config.textColor}
            ${config.animate ? 'animate-spin' : ''}
          `}
          aria-hidden="true"
        >
          {config.icon}
        </span>

        {/* Status Label */}
        <span className={`text-sm font-medium ${config.textColor}`}>
          {config.label}
        </span>

        {/* Retry Button (only for disconnected state) */}
        {status === 'disconnected' && onRetry && (
          <button
            onClick={onRetry}
            className={`
              ml-2 px-2 py-1
              text-xs font-medium
              rounded
              bg-red-500/20
              hover:bg-red-500/30
              ${config.textColor}
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-red-500/50
            `}
            aria-label="Retry connection"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

export default ConnectionStatusIndicator;
